/**
 * The to-do screen's view (core deck.ts TodoView): "List" (today's default) or "Cards". Remembered per person on the
 * server (GET/PUT /me/todo-view), with this device as the fallback, so the choice shows at once and survives a server
 * that is slow or an older one that doesn't have the route.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { todoViewOf, type TodoView } from "@logwave/core";
import type { Api } from "../api.ts";

const KEY = "cc.todoView";

function readLocal(): TodoView {
  try { return todoViewOf(localStorage.getItem(KEY)); } catch { return "list"; }
}
function writeLocal(v: TodoView) {
  try { localStorage.setItem(KEY, v); } catch { /* private window: the server still has it */ }
}

export function useTodoView(api: Api): [TodoView, (v: TodoView) => void] {
  const [view, setView] = useState<TodoView>(readLocal);
  // A choice made in this visit beats a slower answer from the server.
  const chosen = useRef(false);
  useEffect(() => {
    let live = true;
    api.todoView().then((r) => { if (live && !chosen.current) { setView(r.view); writeLocal(r.view); } }, () => undefined);
    return () => { live = false; };
  }, [api]);
  const choose = useCallback((v: TodoView) => {
    chosen.current = true;
    setView(v); writeLocal(v);
    api.setTodoView(v).catch(() => undefined); // the device keeps it if the server can't
  }, [api]);
  return [view, choose];
}
